import React, { useMemo, useState } from 'react';
import { ActionsDropdown, Badge, Button, Card, CardBody, CardHeader, EmptyState, Icon, Spinner, Tooltip } from '@aidkitorg/component-library';
import { useSpinDelay } from 'spin-delay';
import type { Action } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import type { PrecomputedCustomQueryData } from '@aidkitorg/types/lib/admin-panel/distro-dashboard-migration';
import type * as survey from '@aidkitorg/types/lib/survey';
import type { SupportedLanguage } from '@aidkitorg/i18n/lib';
import {
  CustomNumberChart,
  CustomBarChartRenderer,
  CustomPieChartRenderer,
  CustomTableRenderer,
} from './ChartRenderers';
import { useDashboardDownload } from '../hooks/useDashboardDownload';
import { buildLinkGensByCol } from '../../../utils/dashboardUtils';

interface PrecomputedChartCardProps {
  /** Pre-computed data including results and visualization config */
  data?: PrecomputedCustomQueryData | null;
  /** Width of the chart container */
  width?: number;
  /** Height of the chart container */
  height?: number;
  /** Static widget ID from the dashboard widget library */
  staticWidgetId?: string;
  /** When true, shows delete action in card header */
  isEditMode?: boolean;
  /** Callback to delete this component from the dashboard */
  onDelete?: () => void;
  /** Download configuration from the adapted component */
  download?: { filename?: string; downloadOnly?: boolean; asyncDownload?: boolean; downloadPath?: string };
  /** Dashboard data needed for download requests */
  dashboard?: string | survey.Dashboard;
  /** Current language */
  lang?: SupportedLanguage;
  /** When true, shows a loading spinner */
  loading?: boolean;
  /** Error message to display instead of chart content */
  error?: string | null;
  /**
   * Original distro component this card is rendering. Used to extract LinkTo
   * column metadata (e.g. from `Applicant Table.columns`) so Table cells can
   * render `LinkTo` URLs as labeled link buttons. The flattened
   * `PrecomputedCustomQueryData` doesn't carry this, so it's passed alongside.
   */
  originalComponent?: survey.DashboardComponent;
  /** Callback to refresh (re-fetch) the component data with cache bypass */
  onRefresh?: () => Promise<void>;
  /** Timestamp (ms) when the component data was last fetched from the server */
  lastUpdated?: number;
}

/**
 * Renders a chart from pre-computed results (used for CustomQuery components).
 * This bypasses the translation API since CustomQuery uses raw SQL that's
 * already been executed by the distro dashboard API.
 */
export const PrecomputedChartCard: React.FC<PrecomputedChartCardProps> = ({
  data,
  width = 400,
  height = 300,
  staticWidgetId,
  isEditMode,
  onDelete,
  download,
  dashboard,
  lang,
  loading,
  error,
  originalComponent,
  onRefresh,
  lastUpdated,
}) => {
  const L = useLocalizedStrings();
  const results = data?.results;
  const visualization = data?.visualization;
  const title = data?.title;
  const vizKind = visualization?.kind;

  const formatLastFetched = (timestamp?: number) => {
    if (!timestamp) return undefined;
    const date = new Date(timestamp);
    return `${L.admin_panel.payments.last_updated}: ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`;
  };

  // Download via shared hook
  const { downloadInProgress, handleDownload } = useDashboardDownload(
    download?.filename && dashboard
      ? {
        filename: download.filename,
        downloadPath: download.downloadPath || '',
        asyncDownload: download.asyncDownload,
        dashboard,
        lang,
      }
      : null,
  );

  // Calculate chart height (reserve space for header + card padding)
  const headerHeight = 84;
  const chartHeight = Math.max(height - headerHeight, 80);

  // Pull LinkTo column metadata from the original component (when it has a
  // `columns` array, e.g. an `Applicant Table`). `Custom Query` carries no
  // column config, so this will return an empty map for that case.
  const linkGensByCol = useMemo(() => {
    const cols = originalComponent && 'columns' in originalComponent
      ? (originalComponent as { columns?: any[] }).columns
      : undefined;
    return buildLinkGensByCol(cols);
  }, [originalComponent]);

  // Extract title from visualization.
  // Only fall back to 'Custom Query' once data has actually loaded —
  // otherwise deferred cards flash "Custom Query" before the real title arrives.
  const displayTitle = useMemo(() => {
    if (title) return title;
    if (visualization && 'title' in visualization && visualization.title) {
      const t = visualization.title;
      if (typeof t === 'string') return t;
      if (typeof t === 'object' && 'en' in t) return t.en;
    }
    return data ? 'Custom Query' : undefined;
  }, [title, visualization, data]);

  // Refresh handler
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (!onRefresh) return;
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  };

  // Build actions array: download (if applicable) + refresh + delete (if edit mode)
  const actions: Action<PrecomputedCustomQueryData>[] = [];
  if (!isEditMode && onRefresh) {
    actions.push({
      label: 'Refresh',
      onClick: handleRefresh,
      icon: 'ArrowPathIcon',
      tooltip: formatLastFetched(lastUpdated),
    });
  }
  if (download?.filename && dashboard) {
    actions.push({
      label: L.dashboard.download,
      onClick: handleDownload,
      icon: 'ArrowDownTrayIcon',
      disableWhen: () => downloadInProgress,
    });
  }
  if (isEditMode && onDelete) {
    actions.push({
      label: L.admin_panel.dashboards.delete,
      onClick: onDelete,
      icon: 'TrashIcon',
      variant: 'warn' as const,
    });
  }

  const lastFetchedTooltip = formatLastFetched(lastUpdated);
  const refreshOnlyButton = onRefresh && !isEditMode && actions.length === 1
    ? (
      <Tooltip text={lastFetchedTooltip || 'Refresh'} variant="dark" asChild>
        <Button
          variant="unstyled"
          size="sm"
          icon={<Icon name="ArrowPathIcon" size="sm" strokeWidth={2} className={isRefreshing ? 'animate-spin' : ''} />}
          iconPlacement="none"
          onClick={handleRefresh}
          isDisabled={isRefreshing}
          className="text-gray-600 hover:text-gray-800"
          aria-label="Refresh"
        />
      </Tooltip>
    )
    : undefined;

  const actionButton = refreshOnlyButton
    ?? (actions.length > 0 && data
      ? <ActionsDropdown actions={actions} item={data} />
      : undefined);

  const showSpinner = useSpinDelay(!!loading, { delay: 250, minDuration: 0, ssr: false });

  const renderContent = () => {
    if (loading) {
      return showSpinner ? <Spinner size="small" /> : null;
    }
    if (error) {
      return <div className="text-red-400 text-sm text-center px-4">{error}</div>;
    }
    if (!data) {
      return <EmptyState variant="widget" />;
    }
    return renderChart();
  };

  // Render the appropriate chart based on visualization kind
  const renderChart = () => {
    switch (vizKind) {
      case 'Custom Number':
        return <CustomNumberChart results={results} visualization={visualization as survey.CustomNumber} />;

      case 'Custom Bar Chart':
        return (
          <CustomBarChartRenderer
            results={results as any[]}
            visualization={visualization as survey.CustomBarChart}
            height={chartHeight}
            title={displayTitle ?? ''}
          />
        );

      case 'Custom Pie Chart':
        return (
          <CustomPieChartRenderer
            results={results as any[]}
            visualization={visualization as survey.CustomPieChart}
            height={chartHeight}
            title={displayTitle ?? ''}
          />
        );

      case 'Table':
        return (
          <CustomTableRenderer
            results={results as any[]}
            visualization={visualization as survey.Table}
            height={chartHeight}
            linkGensByCol={linkGensByCol}
            staticColumnHeaders={data?.staticColumnHeaders}
          />
        );

      default:
        return (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-sm">Unsupported visualization: {vizKind}</div>
          </div>
        );
    }
  };

  const isTable = vizKind === 'Table';

  return (
    <Card className="rounded-md h-full">
      <CardHeader
        title={
          <div className={`flex flex-wrap items-center gap-2 break-normal px-4 ${isTable ? 'text-lg font-semibold' : 'text-sm'} ${displayTitle ? 'py-2' : 'py-1'}`}>
            {displayTitle && <span>{displayTitle}</span>}
            {staticWidgetId && <Badge variant="neutral">{L.admin_panel.dashboards.static_widget}</Badge>}
          </div>
        }
        actionButton={actionButton}
      />
      <CardBody className="flex-1 flex min-h-0">
        <div className={isTable && !loading && !error ? "flex-1 min-h-0 overflow-auto" : "flex-1 flex items-center justify-center"}>
          {renderContent()}
        </div>
      </CardBody>
    </Card>
  );
};
